// The third-person orbit rig (decision 0043): free orbit from look input, a damped follow with look-ahead, a gentle
// drift back behind a moving target that waits while the player steers, profiles with timed blends, and the
// collision stack. It is a view (decision 0010): it runs once per rendered frame, after the frame's sim steps, and
// reads the target interpolated at the frame's alpha. It never steps with the sim and the sim never reads it, except
// for its yaw, which a controller samples to turn a stick into a world direction (and which a save keeps).
//
// Clocks (decision 0011):
//   look input       applied as it comes (the game calls `look` in its input phase, on real time), so it stays live
//                    through hit-stop;
//   follow, lead,    the frame's scaled dt (real seconds × the time scale), so they slow and freeze with the sim in a
//   drift back       hit-stop, like the target;
//   arm, blends,     real dt, so the camera keeps out of walls and a blend finishes while the sim is slowed or paused.
//   fades
import type * as THREE from "three";
import type { BodyId } from "@rpg/collision";
import { nearCornerDistance, solveArm, type ArmOptions, type ArmSolve, type ArmWorld } from "./arm";
import { CAMERA_FAULTS } from "./faults";
import { add, clamp, damp, orbitDirection, rightOf, smoothstep, wrapAngle, yawOf, type Vec3 } from "./math";
import { ProfileStack, type BlendOptions } from "./profiles";
import { CameraShake, type ShakeOffset, type ShakeSettings } from "./shake";
import type { CameraTarget } from "./target";

const IDENTITY = { x: 0, y: 0, z: 0, w: 1 } as const;
/** The guard ball is this much bigger than the near plane's corner distance, so a camera it stops is clear of contact (m). */
const GUARD_SKIN = 0.01;

/** The rig's own numbers (profiles hold the framing; these hold the collision stack). All from the game. */
export interface RigSettings {
  /** Added to the near plane's corner distance for the probe ball's radius (m). */
  probeMargin: number;
  /** Bodies narrower than this across the view are looked past (m). */
  thinWidth: number;
  /** Fadeable bodies narrower than this fade instead of moving the camera (m). */
  fadeWidth: number;
  /** The arm's rates (1/s, exponential, real time): pulling in, easing back out. In must be faster than out. */
  armIn: number;
  armOut: number;
  /** The fastest the arm pulls in (m/s; 0 for no cap). The guard still stops it at once short of a collider. */
  armInSpeed: number;
  /** The shortest arm (m): the target's radius plus the near plane, so the camera stays out of the target. Below it
   *  the rig cranes up instead of pulling in further. */
  minArm: number;
  /** Predictive casts from where the target will be after these times (s): the arm shortens before an occluder cuts
   *  into view. Empty for none. */
  predict: readonly number[];
  /** Extra pitch the crane tries, in order, when the arm is shorter than minArm (rad). */
  craneSteps: readonly number[];
  /** A faded body's opacity, and the rate (1/s) it fades out and back in. */
  fadeOpacity: number;
  fadeRate: number;
  /** Under this arm length (m) the target itself starts to fade (opacity 0 at minArm), so the camera never shows
   *  the inside of the body. */
  targetFadeStart: number;
}

/** Where the arm meets the world: the collision world and the game's filters. */
export interface RigCollision {
  world: ArmWorld;
  /** Layers that stop or fade the camera (default every layer). */
  layers?: readonly string[];
  /** The followed body's ID (never hit). */
  exclude?: BodyId;
  /** Which bodies may fade instead of moving the camera. */
  canFade?: ArmOptions["canFade"];
}

/** One rendered frame's timing, as the runtime's frame context gives it. */
export interface FrameTiming {
  /** Real seconds this frame. */
  realDt: number;
  /** Scaled seconds this frame (real × the time scale; slows in hit-stop). */
  dt: number;
  /** Interpolation between the previous sim step (0) and the newest (1). */
  alpha: number;
  /** Sim steps this frame took, and their sim seconds. */
  steps: number;
  simDt: number;
}

/** What the rig did on its last frame (for the harness and the debug overlay). */
export interface RigFrame {
  yaw: number;
  pitch: number;
  /** The arm now, the length wanted by the profile, and the length free of collisions this frame (m). */
  arm: number;
  want: number;
  allowed: number;
  /** The shortest free length over the predictive casts (m). */
  predicted: number;
  crane: number;
  /** The pivot the camera looks at, and the camera's position. */
  pivot: Vec3;
  position: Vec3;
  fov: number;
  blocker: BodyId | null;
  thin: BodyId[];
  faded: BodyId[];
  guarded: BodyId | null;
  /** The followed target's opacity (fades as the camera closes in). */
  targetOpacity: number;
  /** This frame cut instead of moving smoothly (a snap, a teleported target, a forced cut). */
  cut: boolean;
  /** Queries this frame. */
  casts: number;
  /** What the shake and kick added on top of `position` this frame (zero with reduce motion or a zero slider). */
  shake: ShakeOffset;
}

/** The rig's saved state: the yaw a controller reads, the player's pitch, the profile requests. JSON-safe. */
export interface RigSnapshot {
  yaw: number;
  pitchOffset: number;
  profiles: Record<string, string>;
}

/** Build options. */
export interface CameraRigOptions {
  camera: THREE.PerspectiveCamera;
  target: CameraTarget;
  profiles: ProfileStack;
  settings: RigSettings;
  collision?: RigCollision | null;
  /** The starting yaw (rad). */
  yaw?: number;
  /** Shake and kick numbers (default: none). */
  shake?: ShakeSettings;
}

/** The orbit rig. Call `look` from the input phase, `update` once per rendered frame after the sim steps. */
export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  readonly profiles: ProfileStack;
  settings: RigSettings;
  collision: RigCollision | null;
  private target: CameraTarget;
  private _yaw: number;
  private pitchOffset = 0;
  private pitchOffsetAtBlend = 0;
  private lookedInBlend = false;
  private blendKey = "";
  private pivot: Vec3 = { x: 0, y: 0, z: 0 };
  private lead = { x: 0, z: 0 };
  private arm = 0;
  private crane = 0;
  private sinceLook = 1e9;
  private calm = 0;
  private lastFacing: number | null = null;
  private cuts = -1;
  private focus: Vec3 | null = null;
  private fadeMap = new Map<BodyId, number>();
  private last: RigFrame;
  private placed = false;
  /** Forced cuts so far (the camera cut through a looked-past body rather than end in the target). */
  forcedCuts = 0;
  /** Shake, kick and FOV punch, layered on top of the framed camera (see shake.ts). */
  readonly shake: CameraShake;

  constructor(o: CameraRigOptions) {
    this.camera = o.camera;
    this.target = o.target;
    this.profiles = o.profiles;
    this.settings = o.settings;
    this.collision = o.collision ?? null;
    this._yaw = o.yaw ?? 0;
    this.shake = new CameraShake(o.shake);
    const p = this.profiles.value;
    this.arm = p.distance;
    this.last = {
      yaw: this._yaw,
      pitch: p.pitch,
      arm: this.arm,
      want: p.distance,
      allowed: p.distance,
      predicted: p.distance,
      crane: 0,
      pivot: { ...this.pivot },
      position: { x: 0, y: 0, z: 0 },
      fov: p.fov,
      blocker: null,
      thin: [],
      faded: [],
      guarded: null,
      targetOpacity: 1,
      cut: false,
      casts: 0,
      shake: this.shake.current(),
    };
  }

  /** The orbit yaw (rad; the camera looks along `forwardOf(yaw)`). A controller reads it to aim the stick. */
  get yaw(): number {
    return this._yaw;
  }

  /** Set the yaw outright (a cut, a scripted shot). */
  set yaw(v: number) {
    this._yaw = wrapAngle(v);
  }

  /** The pitch the camera is at (rad): the profile's rest pitch plus the player's offset, clamped to its range. */
  get pitch(): number {
    const p = this.profiles.value;
    return clamp(p.pitch + this.pitchOffset, p.pitchMin, p.pitchMax);
  }

  /** Follow another target (cuts to it). */
  setTarget(t: CameraTarget): void {
    this.target = t;
    this.placed = false;
  }

  /** A point to frame alongside the target (a combat target, a speaker), or null. Used by profiles with focus. */
  setFocus(p: Vec3 | null): void {
    this.focus = p ? { ...p } : null;
  }

  /** The player's orbit input (rad): `dYaw` > 0 turns the view right, `dPitch` > 0 raises the camera (looks down). */
  look(dYaw: number, dPitch: number): void {
    if (dYaw === 0 && dPitch === 0) return;
    this.sinceLook = 0;
    this.lookedInBlend = true;
    this._yaw = wrapAngle(this._yaw - dYaw);
    const p = this.profiles.value;
    this.pitchOffset = clamp(p.pitch + this.pitchOffset + dPitch, p.pitchMin, p.pitchMax) - p.pitch;
  }

  /** Ask for a profile (see `ProfileStack.request`). The player's pitch offset blends back to the new rest pitch. */
  request(name: string, o?: BlendOptions): void {
    this.profiles.request(name, o);
  }

  /** Release a profile layer (see `ProfileStack.release`). */
  release(layer: string, o?: Omit<BlendOptions, "layer">): void {
    this.profiles.release(layer, o);
  }

  /** Cut: put the camera where it should be now, with no follow lag, look-ahead or arm easing. */
  snap(): void {
    this.placed = false;
  }

  /** What the rig did on its last frame. */
  frame(): Readonly<RigFrame> {
    return this.last;
  }

  /** Faded bodies and their opacity now (1 entries are dropped). */
  fades(): ReadonlyMap<BodyId, number> {
    return this.fadeMap;
  }

  snapshot(): RigSnapshot {
    return { yaw: this._yaw, pitchOffset: this.pitchOffset, profiles: this.profiles.snapshot() };
  }

  restore(s: RigSnapshot): void {
    this._yaw = s.yaw;
    this.pitchOffset = s.pitchOffset;
    this.profiles.restore(s.profiles);
    this.placed = false;
  }

  /** Once per rendered frame, after the frame's sim steps (the runtime's late phase). */
  update(f: FrameTiming): RigFrame {
    const stepped = CAMERA_FAULTS.steppedWithSim;
    // planted defect: a camera stepped with the sim reads the newest step and moves only when steps ran
    if (stepped && f.steps === 0 && this.placed) return this.last;
    const alpha = stepped ? 1 : f.alpha;
    const dt = stepped ? f.simDt : Math.max(0, f.dt);
    const realDt = stepped ? f.simDt : Math.max(0, f.realDt);
    const s = this.settings;

    const p = this.profiles.update(realDt);
    let casts0 = 0;
    const blend = this.profiles.state();
    const key = `${blend.from}>${blend.active}@${blend.duration}`;
    if (key !== this.blendKey) {
      this.blendKey = key;
      this.pitchOffsetAtBlend = this.pitchOffset;
      this.lookedInBlend = false;
    }
    // a blend carries the player's pitch offset back to the new profile's rest pitch, unless the player looks
    if (!this.lookedInBlend && blend.duration > 0) this.pitchOffset = this.pitchOffsetAtBlend * (1 - smoothstep(blend.progress));

    const t = this.target.sample(alpha);
    const cuts = this.target.cuts ?? 0;
    const cut = !this.placed || cuts !== this.cuts;
    this.cuts = cuts;
    const speed = Math.hypot(t.velocity.x, t.velocity.z);
    const facing = t.facing ?? (speed > 0.1 ? yawOf(t.velocity.x, t.velocity.z) : (this.lastFacing ?? this._yaw));

    // drift back behind the target: waits for the player's last look, ramps in after the target stops turning
    this.sinceLook += realDt;
    if (dt > 0) {
      const turnRate = this.lastFacing === null ? 0 : Math.abs(wrapAngle(facing - this.lastFacing)) / dt;
      this.calm = turnRate > p.turningRate ? 0 : this.calm + dt;
    }
    this.lastFacing = facing;
    if (!cut && p.recentreRate > 0 && speed > p.recentreSpeed && this.sinceLook > p.recentreDelay) {
      const w = p.recentreRamp > 0 ? Math.min(1, this.calm / p.recentreRamp) : 1;
      this._yaw = wrapAngle(this._yaw + wrapAngle(facing - this._yaw) * damp(p.recentreRate * w, dt));
    }
    if (this.focus && p.focusYaw > 0) {
      const want = yawOf(this.focus.x - t.position.x, this.focus.z - t.position.z);
      this._yaw = wrapAngle(this._yaw + wrapAngle(want - this._yaw) * damp(p.focusYaw, dt));
    }

    // look-ahead along the velocity, then the damped follow of the pivot
    let lx = t.velocity.x * p.leadTime;
    let lz = t.velocity.z * p.leadTime;
    const ll = Math.hypot(lx, lz);
    if (ll > p.leadMax) {
      lx *= p.leadMax / ll;
      lz *= p.leadMax / ll;
    }
    const want: Vec3 = { x: t.position.x, y: t.position.y + p.pivotHeight, z: t.position.z };
    if (cut) {
      this.lead = { x: 0, z: 0 };
      this.pivot = { ...want };
    } else {
      const kl = damp(p.leadRate, dt);
      this.lead.x += (lx - this.lead.x) * kl;
      this.lead.z += (lz - this.lead.z) * kl;
      const kh = damp(p.followH, dt);
      const kv = damp(t.grounded || t.velocity.y < -p.fastFall ? p.followV : p.followVAir, dt);
      this.pivot.x += (want.x + this.lead.x - this.pivot.x) * kh;
      this.pivot.z += (want.z + this.lead.z - this.pivot.z) * kh;
      this.pivot.y += (want.y - this.pivot.y) * kv;
    }

    // the framed pivot: towards the focus, then over the shoulder
    let view: Vec3 = { ...this.pivot };
    if (this.focus && p.focusWeight > 0) {
      const fy = this.focus.y + p.pivotHeight;
      view = { x: view.x + (this.focus.x - view.x) * p.focusWeight, y: view.y + (fy - view.y) * p.focusWeight, z: view.z + (this.focus.z - view.z) * p.focusWeight };
    }
    if (p.shoulder !== 0) view = add(view, rightOf(this._yaw), p.shoulder);

    // the collision stack
    const radius = nearCornerDistance(this.camera.near, p.fov, this.camera.aspect) + s.probeMargin;
    const opts: ArmOptions | null = this.collision
      ? { radius, layers: this.collision.layers, exclude: this.collision.exclude, thinWidth: s.thinWidth, fadeWidth: s.fadeWidth, canFade: this.collision.canFade }
      : null;
    // the pivot stays out of the world: the look-ahead, the follow lag, the shoulder and the focus are offsets from the
    // target's head, clipped where a ball from the head meets anything but a thin body (a pivot led into a wall or a
    // bush would leave the arm no room)
    if (this.collision && opts && !CAMERA_FAULTS.noCollision) {
      const w = this.collision.world;
      const head = { x: t.position.x, y: t.position.y + p.pivotHeight, z: t.position.z };
      const off = { x: view.x - head.x, y: view.y - head.y, z: view.z - head.z };
      const len = Math.hypot(off.x, off.y, off.z);
      if (len > 1e-4) {
        casts0++;
        const hit = w.castShape({ type: "ball", radius }, head, IDENTITY, off, len, { layers: opts.layers, exclude: opts.exclude, filter: (b) => w.stickWidth(b) >= s.thinWidth });
        if (hit) view = add(head, off, Math.max(0, hit.distance) / len);
      }
    }
    const world = this.collision?.world ?? null;
    const solve = (from: Vec3, extraPitch: number): ArmSolve =>
      opts ? solveArm(world, from, orbitDirection(this._yaw, Math.min(1.5, this.pitch + extraPitch)), p.distance, opts) : { allowed: p.distance, blocker: null, thin: [], faded: [], guarded: null, casts: 0 };

    // the crane: in a tight spot, lift the camera instead of pulling it into the target
    let now = solve(view, this.crane);
    let casts = casts0 + now.casts;
    let craneWant = 0;
    if (now.allowed < s.minArm) {
      craneWant = this.crane;
      for (const c of s.craneSteps) {
        if (c <= this.crane) continue;
        const tryC = solve(view, c);
        casts += tryC.casts;
        craneWant = c;
        if (tryC.allowed >= s.minArm) break;
      }
    } else if (this.crane > 0) {
      // ease back down only when the lower crane would be free too
      const lower = solve(view, 0);
      casts += lower.casts;
      craneWant = lower.allowed >= s.minArm ? 0 : this.crane;
    }
    const prevCrane = this.crane;
    this.crane = cut ? craneWant : this.crane + (craneWant - this.crane) * damp(craneWant > this.crane ? s.armIn : s.armOut, realDt);
    if (Math.abs(this.crane - prevCrane) > 1e-9) {
      now = solve(view, this.crane);
      casts += now.casts;
    }

    // predictive casts: from where the pivot will be shortly. Its path is clipped minArm short of the first wall, as
    // the target's body would stop it, so a run at a wall doesn't put a predicted pivot against the wall with no room.
    let predicted = p.distance;
    const hv = Math.hypot(t.velocity.x, t.velocity.z);
    if (world && opts && hv > 1e-3) {
      const along = { x: t.velocity.x / hv, y: 0, z: t.velocity.z / hv };
      for (const ahead of s.predict) {
        casts++;
        const block = world.castShape({ type: "ball", radius: Math.max(radius, s.minArm) }, view, IDENTITY, along, hv * ahead, { layers: opts.layers, exclude: opts.exclude });
        const r = solve(add(view, along, block ? block.distance : hv * ahead), this.crane);
        casts += r.casts;
        predicted = Math.min(predicted, r.allowed);
      }
    }

    // the arm: pull in fast, ease out slow; a prediction never pulls it under minArm (only a wall there now does, and
    // then the crane lifts the camera)
    const goal = Math.min(now.allowed, Math.max(predicted, Math.min(s.minArm, now.allowed)), p.distance);
    if (cut) this.arm = Math.min(now.allowed, p.distance);
    else {
      const pullIn = goal < this.arm;
      const rate = pullIn ? s.armIn : CAMERA_FAULTS.equalArmRates ? s.armIn : s.armOut;
      let move = (goal - this.arm) * damp(rate, realDt);
      // a capped pull-in speed, so a big gap (a wall swung into, a roof ahead) closes over a few frames, not one
      if (pullIn && s.armInSpeed > 0) move = Math.max(move, -s.armInSpeed * realDt);
      this.arm += move;
    }
    this.arm = Math.max(0, this.arm);
    // the guard: the camera never ends inside a collider (an arm still easing in behind a wall, a camera passing a
    // looked-past pole): it stops where the line first meets the body it would sit in
    const dir = orbitDirection(this._yaw, Math.min(1.5, this.pitch + this.crane));
    let forced = false;
    if (world && opts && !CAMERA_FAULTS.noCollision) {
      const guard = { type: "ball" as const, radius: radius - s.probeMargin + GUARD_SKIN };
      casts++;
      const inside = world.overlap(guard, add(view, dir, this.arm), IDENTITY, { layers: opts.layers, exclude: opts.exclude });
      let pinnedBy: BodyId | null = null;
      for (const id of inside) {
        casts++;
        const h = world.castShape(guard, view, IDENTITY, dir, this.arm, { layers: opts.layers, filter: (b) => b === id });
        const at = h ? h.distance : 0;
        if (at < this.arm) {
          this.arm = at;
          pinnedBy = id;
        }
      }
      // a forced cut: the target walking into a camera held in front of a body the cast looks past (a pole, a bush:
      // the line beyond it is free, so solveArm allowed more) would put the camera in the target; it cuts through instead
      if (pinnedBy !== null && this.arm < s.minArm && now.allowed >= s.minArm) {
        this.arm = Math.min(now.allowed, p.distance);
        forced = true;
        this.forcedCuts++;
      }
    }

    const pos = add(view, dir, this.arm);
    // shake and kick on top (real time): the whole view shifts by the offset, then turns by the shake's angles
    const sh = this.shake.update(realDt, rightOf(this._yaw), { x: 0, y: 1, z: 0 });
    const o = sh.offset;
    this.camera.position.set(pos.x + o.x, pos.y + o.y, pos.z + o.z);
    this.camera.lookAt(view.x + o.x, view.y + o.y, view.z + o.z);
    if (sh.roll !== 0 || sh.pitch !== 0 || sh.yaw !== 0) {
      this.camera.rotateZ(sh.roll);
      this.camera.rotateX(sh.pitch);
      this.camera.rotateY(sh.yaw);
    }
    const fov = p.fov + sh.fov;
    if (Math.abs(this.camera.fov - fov) > 1e-6) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
    this.camera.updateMatrixWorld();
    this.placed = true;

    // fades: occluders the arm looked past, and the target when the camera is close
    const faded = new Set(now.faded);
    for (const id of faded) if (!this.fadeMap.has(id)) this.fadeMap.set(id, 1);
    const kf = damp(s.fadeRate, realDt);
    for (const [id, o] of this.fadeMap) {
      const goalO = faded.has(id) ? s.fadeOpacity : 1;
      const next = o + (goalO - o) * kf;
      if (!faded.has(id) && next > 0.995) this.fadeMap.delete(id);
      else this.fadeMap.set(id, next);
    }
    const targetOpacity = s.targetFadeStart > s.minArm ? clamp((this.arm - s.minArm) / (s.targetFadeStart - s.minArm), 0, 1) : 1;

    this.last = {
      yaw: this._yaw,
      pitch: this.pitch,
      arm: this.arm,
      want: p.distance,
      allowed: now.allowed,
      predicted,
      crane: this.crane,
      pivot: view,
      position: pos,
      fov: p.fov,
      blocker: now.blocker,
      thin: now.thin,
      faded: now.faded,
      guarded: now.guarded,
      targetOpacity,
      cut: cut || forced,
      casts,
      shake: sh,
    };
    return this.last;
  }
}
